// Extracted verbatim from views/CaseDetail.js (PART C). Imports are the union
// used across the case-detail tabs; unused ones in a given tab are harmless.
import React, { useEffect, useRef, useState } from "react";
import CenteredModal from "../../components/CenteredModal";
import Spinner from "../../components/Spinner";
import Confirm from "../../components/Confirm";
import Modal from "../../components/Modal";
import { TipoPill, EstadoPill, SlaPill, TipoFalloPill, ESTADO_LABEL, PREFER_SUB_TIPO_STATES } from "../../components/Pills";
import { formatTipo } from "../../utils/case_taxonomy";
import { cases, insumos as insumosApi, users as usersApi, moderation as moderationApi } from "../../api/endpoints";
import { useAuth } from "../../auth/AuthContext";
import { useToast } from "../../components/Toast";
import InsumosTab from "../insumos/InsumosTab";
import LitisoftFields from "../../components/LitisoftFields";
import LitisoftIncompleteModal from "../../components/LitisoftIncompleteModal";
import LitisoftMandatoryInline from "../../components/LitisoftMandatoryInline";
import { TERMINO_OPTIONS, formatTermino } from "../../utils/termino";
import { canMarkContestadaDirectly, canRequestModeration, availableContestadaCategorias, CONTESTADA_LABELS as CONTESTADA_LABELS_FULL, rolesOf } from "../../utils/permissions";
import LawyerActionPanel from "../../components/lawyer/LawyerActionPanel";
import ProcesoCarpetasBanner from "../../components/ProcesoCarpetasBanner";
import CarpetaDocumentsPanel from "../../components/CarpetaDocumentsPanel";
import CarpetaStatusBadge from "../../components/CarpetaStatusBadge";
import InlineEditAccionante from "../../components/InlineEditAccionante";
import TipoFalloEditModal from "../../components/TipoFalloEditModal";
import { PERSONA_EDIT_ROLES } from "../Personas";
import { fmt, InlineEdit, hasLawyerRole, MarcarContestada, CaseFlagsToggles, hasSancionesSpecialization, SancionLifecycle, MarcarInsumoDeficiente, SolicitarModeracion, LawyerAgencyButtons, TABS, CONTESTADA_LABELS } from "./shared";

export default function DocumentosTab({ c, user, onChanged }) {
  const toast = useToast();
  const docs = c.documents || [];
  const [replacing, setReplacing] = useState(null);  // doc being replaced
  const [confirmDel, setConfirmDel] = useState(null);
  // feature/single-role-audit — multi-role aware.
  const isAdmin = rolesOf(user).includes("admin");

  async function softDelete(d) {
    try {
      await cases.deleteDocument(c.id, d.id);
      toast.push("Documento eliminado");
      setConfirmDel(null);
      onChanged?.();
    } catch (e) { toast.push(e?.response?.data?.error || "Error", "error"); }
  }
  async function restore(d) {
    try {
      await cases.restoreDocument(c.id, d.id);
      toast.push("Documento restaurado");
      onChanged?.();
    } catch (e) { toast.push(e?.response?.data?.error || "Error", "error"); }
  }

  return (
    <>
      <h3 style={{ marginTop: 0 }}>Documentos del abogado</h3>
      {isAdmin && (
        <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
          Para ver eliminados, abra el caso con <code>?include_deleted=1</code> en la URL (admin).
        </div>
      )}
      {!docs.length ? <p className="muted">El abogado todavía no ha subido documentos.</p> : (
        <table>
          <thead><tr><th>Tipo</th><th>Subido por</th><th>Fecha</th><th>Versiones</th><th></th></tr></thead>
          <tbody>
            {docs.map(d => {
              const versions = Array.isArray(d.previous_file_paths) ? d.previous_file_paths.length : 0;
              const deleted = !!d.deleted_at;
              const canTouch = isAdmin || d.uploaded_by_id === user?.id;
              return (
                <tr key={d.id} style={deleted ? { opacity: 0.5 } : {}}>
                  <td>{d.type} {deleted && <span className="badge danger" style={{ marginLeft: 6 }}>Eliminado</span>}</td>
                  <td>{d.uploaded_by_name || "—"}</td>
                  <td>{fmt(d.created_at)}</td>
                  <td>{versions > 0 ? <span className="muted" style={{ fontSize: 12 }}>Versiones anteriores: {versions}</span> : <span className="muted">—</span>}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {!deleted && canTouch && (
                      <>
                        <button className="ghost sm" title="Reemplazar" onClick={() => setReplacing(d)}>Reemplazar</button>
                        <button className="ghost sm" title="Eliminar" style={{ color: "var(--color-danger)" }} onClick={() => setConfirmDel(d)}>🗑</button>
                      </>
                    )}
                    {deleted && canTouch && (
                      <button className="ghost sm" onClick={() => restore(d)}>Restaurar</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <ReplaceDocModal
        open={!!replacing}
        target={replacing}
        caseId={c.id}
        onClose={() => setReplacing(null)}
        onReplaced={() => { setReplacing(null); onChanged?.(); }}
      />
      <Confirm
        open={!!confirmDel}
        title="Eliminar documento"
        message="¿Eliminar este documento? Podrá recuperarlo después."
        onClose={() => setConfirmDel(null)}
        onConfirm={() => softDelete(confirmDel)}
        confirmText="Eliminar"
        danger
      />
    </>
  );
}

// feature/doc-replace — file replace modal for case_document scope.
function ReplaceDocModal({ open, target, caseId, onClose, onReplaced }) {
  const toast = useToast();
  const [file, setFile] = useState(null);
  const [type, setType] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (target) {
      setFile(null);
      setType(target.type || "");
      setNotes(target.notes || "");
    }
  }, [target]);

  const typeOptions = [["contestacion","Contestación"],["memorial","Memorial"],["sello_radicacion","Sello radicación"],["dictamen_junta","Dictamen junta"],["auto_juzgado","Auto juzgado"],["otro","Otro"]];

  async function submit() {
    if (!file) { toast.push("Seleccione un archivo", "error"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (type) fd.append("type", type);
      if (notes) fd.append("notes", notes);
      await cases.replaceDocument(caseId, target.id, fd);
      toast.push("Documento reemplazado");
      onReplaced?.();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al reemplazar", "error");
    } finally { setBusy(false); }
  }

  if (!target) return null;
  return (
    <Modal
      open={open}
      title="Reemplazar archivo"
      onClose={busy ? undefined : onClose}
      footer={<>
        <button className="secondary" disabled={busy} onClick={onClose}>Cancelar</button>
        <button disabled={busy || !file} onClick={submit}>{busy && <Spinner color="currentColor" />} Reemplazar</button>
      </>}
    >
      <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
        El archivo anterior queda en el historial (audit_log + previous_file_paths).
      </div>
      <div>
        <label>Archivo nuevo</label>
        <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
      </div>
      <div style={{ marginTop: 8 }}>
        <label>Tipo</label>
        <select value={type} onChange={(e) => setType(e.target.value)}>
          {typeOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      <div style={{ marginTop: 8 }}>
        <label>Notas</label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>
    </Modal>
  );
}

// ===========================================================================
// Historial tab
// ===========================================================================
